import './Sidebar.css';

import React from 'react';

import {
  FaCheckCircle,
  FaClipboardList,
  FaComments,
  FaFileInvoiceDollar,
  FaSearch,
  FaTimesCircle,
} from 'react-icons/fa';
import { useLocation } from 'react-router-dom';

const Sidebar = ({ 
  activeSection, 
  setActiveSection, 
  formProgress, 
  billsUploaded, 
  analysisSubmitted 
}) => {
  const location = useLocation();
  
  // Track if forms are completed
  const formsCompleted = formProgress.contract && formProgress.nda && formProgress.loa;

  // Get status icon based on completion
  const getStatusIcon = (completed) => {
    return completed ? 
      <FaCheckCircle className="status-icon completed" /> : 
      <FaTimesCircle className="status-icon incomplete" />;
  };

  // Function to navigate to a section
  const navigateToSection = (section) => {
    if (section === activeSection) return;
    
    if (section === 'utility-bills' && !formsCompleted) {
      // Don't allow navigation to utility bills if forms are not complete
      return;
    }
    
    if (section === 'tracking' && !analysisSubmitted) {
      // Don't allow navigation to tracking if analysis is not submitted
      return;
    }
    
    // Set the active section
    setActiveSection(section);
  };

  return (
    <div className="sidebar">
      <div className="sidebar-content">
        <ul className="sidebar-menu">
          <li 
            className={`sidebar-menu-item ${activeSection === 'forms' ? 'active' : ''}`}
            onClick={() => navigateToSection('forms')}
            data-section="forms"
          >
            <div className="menu-item-content">
              <FaClipboardList className="menu-icon" />
              <span className="menu-text">Forms</span>
              {getStatusIcon(formsCompleted)}
            </div>
          </li>
          
          <li 
            className={`sidebar-menu-item ${activeSection === 'utility-bills' ? 'active' : ''} ${!formsCompleted ? 'disabled' : ''}`}
            onClick={() => navigateToSection('utility-bills')}
            data-section="utility-bills"
            id="utility-bills-tab"
          >
            <div className="menu-item-content">
              <FaFileInvoiceDollar className="menu-icon" />
              <span className="menu-text">Utility Bills</span>
              {getStatusIcon(billsUploaded)}
            </div>
          </li>
          
          <li 
            className={`sidebar-menu-item ${activeSection === 'tracking' ? 'active' : ''} ${!analysisSubmitted ? 'disabled' : ''}`}
            onClick={() => navigateToSection('tracking')}
            data-section="tracking"
          >
            <div className="menu-item-content">
              <FaSearch className="menu-icon" />
              <span className="menu-text">Tracking</span>
              {getStatusIcon(analysisSubmitted)}
            </div>
          </li>
          
          <li className="sidebar-divider"></li>
          
          <li 
            className={`sidebar-menu-item ${activeSection === 'messages' ? 'active' : ''}`}
            onClick={() => navigateToSection('messages')}
            data-section="messages"
          >
            <div className="menu-item-content">
              <FaComments className="menu-icon" />
              <span className="menu-text">Messages</span>
            </div>
          </li>
        </ul>
        
        <div className="sidebar-progress">
          <h3 className="progress-title">Your Progress</h3>
          <div className="progress-bar-container">
            <div 
              className="progress-bar" 
              style={{ 
                width: `${calculateProgress(formsCompleted, billsUploaded, analysisSubmitted)}%` 
              }}
            ></div>
          </div>
          <div className="progress-steps">
            <div className={`progress-step ${formsCompleted ? 'completed' : activeSection === 'forms' ? 'active' : ''}`}>
              <span className="step-number">1</span>
              <span className="step-label">Forms</span>
            </div>
            <div className={`progress-step ${billsUploaded ? 'completed' : activeSection === 'utility-bills' ? 'active' : ''}`}>
              <span className="step-number">2</span>
              <span className="step-label">Bills</span>
            </div>
            <div className={`progress-step ${analysisSubmitted ? 'completed' : activeSection === 'tracking' ? 'active' : ''}`}>
              <span className="step-number">3</span>
              <span className="step-label">Analysis</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Calculate progress percentage based on completed steps
const calculateProgress = (formsCompleted, billsUploaded, analysisSubmitted) => {
  let progress = 0;
  if (formsCompleted) progress += 33.33;
  if (billsUploaded) progress += 33.33;
  if (analysisSubmitted) progress += 33.34;
  return progress;
};

export default Sidebar;